import { useCallback, useEffect, useState } from 'react';
import {
    fetchMyContact, fetchVapidPublicKey, registerMyPushSubscription, unregisterMyPushSubscription, MyContact,
} from '../services/alertsApi';
import { pushSupported, urlBase64ToUint8Array } from '../utils/push';

/**
 * Push på den här telefonen för den inloggades egen kontakt (samma e-post som användaren).
 * Används av rutan efter inloggning och av valet i mobilmenyn.
 *
 * available: push fungerar här och det finns en egen kontakt att koppla till.
 * on: den här webbläsaren har tillstånd, en prenumeration, och den är kopplad till kontakten.
 */
export function useMyPush(active: boolean) {
    const supported = pushSupported();
    const [contact, setContact] = useState<MyContact | null>(null);
    const [on, setOn] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        if (!supported || !active) return;
        try {
            const c = await fetchMyContact();
            setContact(c);
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setOn(!!c && !!sub && Notification.permission === 'granted'
                && c.notify_push && c.push_endpoints.includes(sub.endpoint));
        } catch {
            setContact(null);
            setOn(false);
        }
    }, [supported, active]);

    useEffect(() => { refresh(); }, [refresh]);

    /** Måste anropas från ett klick: webbläsaren frågar bara om tillstånd då. */
    const enable = useCallback(async () => {
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') {
                window.alert('Notiser är blockerade för appen. Tillåt dem i telefonens inställningar och försök igen.');
                return;
            }
            const key = await fetchVapidPublicKey();
            if (!key) { window.alert('Notiser är inte inställda på servern.'); return; }
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription()
                ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
            await registerMyPushSubscription(sub);
            await refresh();
        } catch (e: any) {
            window.alert(`Kunde inte slå på notiser: ${e?.response?.data?.message || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [refresh]);

    const disable = useCallback(async () => {
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await unregisterMyPushSubscription(sub.endpoint);
                await sub.unsubscribe();
            }
            await refresh();
        } catch (e: any) {
            window.alert(`Kunde inte stänga av notiser: ${e?.response?.data?.message || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [refresh]);

    return { available: supported && !!contact, on, busy, enable, disable };
}
